// Bryntum's theme stylesheet, following the dashboard's light/dark mode (package-private; read by
// ../components/BaseGanttChart/setup.js). Light = Material (the look issue-analysis shipped),
// dark = Classic-Dark — Bryntum 6.2 ships no dark Material (its themes: classic, classic-light,
// classic-dark, material, stockholm).
//
// Why a managed <style> and not Bryntum's own `DomHelper.setTheme(name)`: setTheme swaps a
// `<link data-bryntum-theme href=".../gantt.<theme>.css">` by string-replacing the theme name in
// its href, so it needs Bryntum's CSS and fonts served raw under their own file names. Under Vite
// that means `?url` imports, which (a) hash the file names (the replaced href 404s in a build) and
// (b) emit the CSS unprocessed, so its relative `url("fonts/…")` breaks once the CSS sits in
// `assets/`. And two plain `import '…css'` would inject BOTH theme sheets globally — they conflict
// (6.2's themes are complete compiled stylesheets with their colours baked in, not CSS-variable
// themes, so one cannot sit on top of the other).
//
// So each theme is imported with Vite's `?inline`: the CSS goes through Vite's CSS pipeline (its
// `url()`s rebased — `/@fs/…` in dev, emitted hashed assets in a build) and comes back as a string
// WITHOUT being injected. Exactly one <style id="rdrive-bryntum-theme"> holds the active theme's
// text; a mode change swaps that text. Each theme is a dynamic import — its own lazily loaded
// chunk — so only the active theme is downloaded, and an app page that never mounts a Gantt
// downloads neither.
//
// The element is shared by every Gantt instance and left in place on unmount, so navigating away
// and back re-uses it with no flash (and no re-download). After a swap, Bryntum's global `theme`
// event is fired — what its own setTheme fires — so live grids re-measure and floating roots
// (tooltips, menus) swap their `b-theme-*` class. Bryntum's cached `DomHelper.themeInfo` (module
// private; only setTheme resets it) keeps the first theme's name: it only picks the class of a
// float root created after a toggle and a few widgets this Gantt does not use.
import { GlobalEvents } from '@bryntum/gantt';

// The id of the one managed element
export const BRYNTUM_THEME_STYLE_ID = 'rdrive-bryntum-theme';

// mode → Bryntum's theme name (as its `.b-theme-info` carries it) + the stylesheet's loader
const THEMES = {
  light: {
    name: 'Material',
    load: () => import('@bryntum/gantt/gantt.material.css?inline')
  },
  dark: {
    name: 'Classic-Dark',
    load: () => import('@bryntum/gantt/gantt.classic-dark.css?inline')
  }
};

// The mode whose CSS is in the element, and the latest request ({ key, promise }). The latest
// request wins: an older load that resolves after it is dropped, and its caller waits for the
// latest one instead — so a caller (the base's mount) never resumes with no stylesheet in.
let applied = null;
let latest = null;

/**
 * Put the Bryntum theme for `mode` in the managed <style>, creating the element on first use.
 * Resolves once a theme is active — this one, or the one a newer call asked for meanwhile —
 * immediately when it already is. Anything but `'dark'` is light. A failed load is logged and
 * leaves the current theme in place (a later call retries).
 *
 * @param {string} mode  `'light'` | `'dark'` — `useChartTheme().value.mode`
 * @returns {Promise<void>}
 */
export function applyBryntumTheme(mode) {
  const key = mode === 'dark' ? 'dark' : 'light';
  if (latest && latest.key === key) return latest.promise;
  if (!latest && applied === key) return Promise.resolve();

  const request = { key };
  request.promise = (async () => {
    let css;
    try {
      css = (await THEMES[key].load()).default;
    } catch (error) {
      console.error(`Error loading the Bryntum ${THEMES[key].name} theme:`, error);
      if (latest === request) latest = null;
      return;
    }
    // Superseded while loading: resolve when the newest request is in instead
    if (latest !== request) return latest ? latest.promise : undefined;

    let element = document.getElementById(BRYNTUM_THEME_STYLE_ID);
    if (!element) {
      element = document.createElement('style');
      element.id = BRYNTUM_THEME_STYLE_ID;
      document.head.appendChild(element);
    }
    element.textContent = css;

    const previous = applied;
    applied = key;
    if (previous && previous !== key) {
      GlobalEvents.trigger('theme', { theme: THEMES[key].name, prev: THEMES[previous].name });
    }
  })();
  latest = request;
  return request.promise;
}
